'use client'

import { ext } from '@/utils/linkTarget'
import { useState } from 'react'
import Container from '@/components/templates/container'
import { PortableText } from 'next-sanity'
import editorRule from '@/components/atoms/editorRule'
import titleCase from '@/utils/titleCase'
import { body, h2, solidBtn, outlineBtn } from './index'

const REZERV = 'https://mahimabali.rezerv.co'
const WHATSAPP = 'https://wa.me/6281337582672'
const textLink = 'pb-1 border-b border-olive font-chivoMono text-olive text-[13px] uppercase tracking-widest hover:text-umber'
const pill = (active) =>
  `shrink-0 min-h-[44px] px-5 py-2.5 border font-chivoMono text-[12px] uppercase tracking-widest whitespace-nowrap transition-colors duration-200 ${
    active ? 'bg-forest border-forest text-background' : 'border-khaki/40 text-umber hover:border-forest'
  }`

// ── Resident plans from Rezerv: price per month, upfront price and perks ──

const planName = (p) => (p.months === 12 ? '1 Year' : p.months ? `${p.months} Months` : titleCase(p.title))

const PerkList = ({ perks, best }) => {
  const { included = [], discounts = [], freeze } = perks || {}
  const heading = `font-chivoMono text-[10px] uppercase tracking-widest mb-3 ${best ? 'text-dune' : 'text-khaki'}`
  const text = `font-chivoMono text-[13px] leading-snug ${best ? 'text-background/85' : 'text-umber'}`
  const rule = best ? 'border-background/20' : 'border-dune'
  return (
    <div className={`flex flex-col gap-6 pt-5 border-t ${rule}`}>
      {included.length > 0 && (
        <div>
          <p className={heading}>Included</p>
          <ul>
            {included.map((perk) => (
              <li key={perk} className={`py-2 border-b border-dashed ${rule} ${text}`}>
                {perk}
              </li>
            ))}
          </ul>
        </div>
      )}
      {discounts.length > 0 && (
        <div>
          <p className={heading}>Member discounts</p>
          <ul>
            {discounts.map((d) => (
              <li key={d.label} className={`flex items-baseline justify-between gap-4 py-2 border-b border-dashed ${rule} ${text}`}>
                <span>{d.label}</span>
                <span className="font-ttDrugsTrial text-[18px] leading-none whitespace-nowrap">{d.value}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {freeze && (
        <p className={`font-chivoMono text-[12px] ${best ? 'text-background/70' : 'text-khaki'}`}>
          Pause your membership for up to {freeze.toLowerCase()}
        </p>
      )}
    </div>
  )
}

const PlanCard = ({ plan, best }) => (
  <div className={`flex flex-col gap-4 p-7 lg:p-9 ${best ? 'bg-forest text-background' : 'border border-dune text-olive'}`}>
    <div className="flex items-start justify-between gap-3">
      <span className={`font-chivoMono text-[11px] uppercase tracking-widest ${best ? 'text-dune' : 'text-khaki'}`}>
        Open Gym Resident · {planName(plan)}
      </span>
      {best && (
        <span className="shrink-0 px-2 py-1 bg-background text-forest font-chivoMono text-[10px] uppercase tracking-widest">Best value</span>
      )}
    </div>
    <div className="flex flex-col gap-1.5">
      <span className="font-ttDrugsTrial text-[30px] lg:text-[36px] leading-none">
        {plan.perMonth || plan.price}
        {plan.perMonth && <span className="font-chivoMono text-[13px] tracking-normal"> / month</span>}
      </span>
      {plan.perMonth && (
        <span className={`font-chivoMono text-[12px] ${best ? 'text-background/70' : 'text-khaki'}`}>
          {plan.price} upfront · taxes included
        </span>
      )}
    </div>
    <PerkList perks={plan.perks} best={best} />
    <a href={plan.url} {...ext(plan.url)}
      className={`mt-auto pt-2 self-start pb-0.5 border-b font-chivoMono text-[12px] uppercase tracking-widest ${
        best ? 'border-background text-background hover:text-dune hover:border-dune' : 'border-olive text-olive hover:text-umber hover:border-umber'
      }`}>
      Join on Rezerv <span aria-hidden="true">→</span>
    </a>
  </div>
)

// ── Memberships and passes ─────────────────────────────────────────────────

// plans: Open Gym Resident plans from Rezerv (utils/rezervMemberships); without them the Sanity featured cards show
export default function GymPricing({ gym, plans, id = 'pricing' }) {
  const [activeTab, setActiveTab] = useState(0)
  if (!gym) return null
  const tabs = gym.gymTabs || []
  const current = tabs[Math.min(activeTab, Math.max(tabs.length - 1, 0))]

  return (
    <Container outerClassName="mt-[100px] lg:mt-[150px]">
      <section id={id} className="scroll-mt-[100px]">
        {/* Heading with the booking buttons on the right */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 lg:gap-20 mb-10 lg:mb-12">
          <div className="flex flex-col gap-5">
            <h2 className={h2}>{titleCase(gym.title || 'Prices')}</h2>
            {gym.description && <p className={`${body} max-w-[560px]`}>{gym.description}</p>}
          </div>
          <div className="flex flex-wrap items-center gap-3 shrink-0">
            <a href={gym.rezervLink || REZERV} {...ext(gym.rezervLink || REZERV)} className={solidBtn}>
              {gym.rezervLabel || 'Join via Rezerv'}
            </a>
            <a href={gym.whatsappLink || WHATSAPP} {...ext(gym.whatsappLink || WHATSAPP)} className={outlineBtn}>
              Book on WhatsApp
            </a>
          </div>
        </div>

        {/* Memberships: live Rezerv resident plans, else the Sanity featured cards */}
        {plans?.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 lg:gap-4 mb-12 lg:mb-14 items-stretch">
            {plans.map((p, i) => <PlanCard key={p.id} plan={p} best={plans.length > 1 && i === plans.length - 1} />)}
          </div>
        ) : gym.gymFeatured?.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 lg:gap-4 mb-12 lg:mb-14">
            {gym.gymFeatured.map((item, i) => (
              <div key={i} className={`flex flex-col gap-3 p-7 lg:p-9 ${item.highlight ? 'bg-forest text-background' : 'border border-dune text-olive'}`}>
                <span className={`font-chivoMono text-[11px] uppercase tracking-widest ${item.highlight ? 'text-dune' : 'text-khaki'}`}>{titleCase(item.title)}</span>
                {item.price && <span className="font-ttDrugsTrial text-[30px] lg:text-[36px] leading-none">{item.price}</span>}
                {item.description && <p className={`font-chivoMono text-[13px] ${item.highlight ? 'text-background/75' : 'text-umber'}`}>{item.description}</p>}
              </div>
            ))}
          </div>
        )}

        {tabs.length > 0 ? (
          <>
            <div role="tablist" aria-label="Price categories" className="flex gap-2 overflow-x-auto scrollbar-hidden mb-6">
              {tabs.map((tab, i) => (
                <button key={i} type="button" role="tab" aria-selected={current === tab} onClick={() => setActiveTab(i)} className={pill(current === tab)}>
                  {tab.tabLabel}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 lg:gap-3">
              {current?.cards?.map((card, i) => (
                <div key={i} className="flex flex-col gap-2 p-4 lg:p-5 border border-dune">
                  <div className="flex items-start justify-between gap-3">
                    <span className="font-ttDrugsTrial text-olive text-[17px] leading-snug">{titleCase(card.title)}</span>
                    {card.bestValue && (
                      <span className="shrink-0 px-2 py-1 bg-forest text-background font-chivoMono text-[10px] uppercase tracking-widest">Best value</span>
                    )}
                  </div>
                  {card.price && <span className="font-ttDrugsTrial text-olive text-[22px] leading-none">{card.price}</span>}
                  {card.description && (
                    <div className="mt-auto pt-2 font-chivoMono text-[12px] text-khaki [&_strong]:font-normal [&_strong]:text-olive">
                      <PortableText value={card.description} components={editorRule()} />
                    </div>
                  )}
                </div>
              ))}
            </div>
            {current?.note && <p className="font-chivoMono text-[13px] text-khaki mt-4">{current.note}</p>}
          </>
        ) : (
          gym.rates?.length > 0 && (
            <div className="border-t border-olive">
              {gym.rates.map((r, i) => (
                <div key={i} className="flex items-baseline justify-between gap-6 py-5 border-b border-dune">
                  <span className="font-chivoMono text-bodyMobile md:text-bodyDesktop text-olive">{r.label}</span>
                  <span className="font-chivoMono text-bodyMobile md:text-bodyDesktop text-umber whitespace-nowrap">{r.price}</span>
                </div>
              ))}
            </div>
          )
        )}

        {gym.note && <p className="font-chivoMono text-[13px] text-khaki mt-4">{gym.note}</p>}

      </section>
    </Container>
  )
}

